import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

import {
  AI_ASSISTANT_DEFAULT_WIDTH,
  clampAssistantWidth,
  persistAssistantWidth,
  readStoredAssistantWidth,
} from './constants';
import {
  aiAssistantReducer,
  createInitialAiAssistantState,
  type AiAssistantActiveSession,
} from './aiAssistantState';
import type {
  AiAssistantFlowType,
  AiAssistantRegisteredContext,
  ProgressNoteContext,
  TreatmentPlanGuidedContext,
  TreatmentPlanRevisionContext,
} from './types';

interface AiAssistantContextValue {
  open: boolean;
  width: number;
  flow: AiAssistantFlowType | null;
  revisionContext: TreatmentPlanRevisionContext | null;
  guidedContext: TreatmentPlanGuidedContext | null;
  progressNoteContext: ProgressNoteContext | null;
  activeSession: AiAssistantActiveSession | null;
  hasUsableContext: boolean;
  openDrawer: (flow?: AiAssistantFlowType) => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  setWidth: (width: number, availableMainWidth?: number) => void;
  resetWidth: () => void;
  startQuickTreatmentPlanFlow: (context: TreatmentPlanGuidedContext) => void;
  startGuidedTreatmentPlanFlow: (context: TreatmentPlanGuidedContext) => void;
  startTreatmentPlanRevisionFlow: () => void;
  startProgressNoteGenerateFlow: () => void;
  openGeneralChat: () => void;
  registerRevisionContext: (context: TreatmentPlanRevisionContext | null) => void;
  registerGuidedContext: (context: TreatmentPlanGuidedContext | null) => void;
  registerProgressNoteContext: (context: ProgressNoteContext | null) => void;
  promoteDocumentIdentity: (documentKey: string, editorSessionKey: string) => void;
  clearAllContext: () => void;
}

const AiAssistantContext = createContext<AiAssistantContextValue | null>(null);

function documentKeyFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/dashboard\/clients\/([^/]+)\/chart-document\/([^/?#]+)/);
  return match ? `${match[1]}:${match[2]}` : null;
}

function patientIdFromPath(pathname: string): string | null {
  return pathname.match(/^\/dashboard\/clients\/([^/]+)/)?.[1] ?? null;
}

export function AiAssistantProvider({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const [width, setWidthState] = useReducer(
    (_prev: number, next: number) => next,
    undefined,
    readStoredAssistantWidth,
  );
  const [state, dispatch] = useReducer(aiAssistantReducer, undefined, createInitialAiAssistantState);

  useEffect(() => {
    dispatch({
      type: 'NAVIGATE',
      pathPatientId: patientIdFromPath(location.pathname),
      pathDocumentIdentity: documentKeyFromPath(location.pathname),
      pathname: location.pathname,
    });
  }, [location.pathname]);

  const setWidth = useCallback((next: number, availableMainWidth = Number.POSITIVE_INFINITY) => {
    const clamped = clampAssistantWidth(next, availableMainWidth);
    setWidthState(clamped);
    persistAssistantWidth(clamped);
  }, []);

  const resetWidth = useCallback(() => setWidth(AI_ASSISTANT_DEFAULT_WIDTH), [setWidth]);
  const openDrawer = useCallback((flow?: AiAssistantFlowType) => {
    dispatch({ type: 'OPEN_DRAWER', flow });
  }, []);
  const closeDrawer = useCallback(() => dispatch({ type: 'CLOSE_DRAWER' }), []);
  const toggleDrawer = useCallback(() => dispatch({ type: 'TOGGLE_DRAWER' }), []);
  const registerRevisionContext = useCallback((context: TreatmentPlanRevisionContext | null) => {
    dispatch({ type: 'REGISTER_REVISION', context });
  }, []);
  const registerGuidedContext = useCallback((context: TreatmentPlanGuidedContext | null) => {
    dispatch({ type: 'REGISTER_GUIDED', context });
  }, []);
  const registerProgressNoteContext = useCallback((context: ProgressNoteContext | null) => {
    dispatch({ type: 'REGISTER_PROGRESS_NOTE', context });
  }, []);
  const startQuickTreatmentPlanFlow = useCallback((context: TreatmentPlanGuidedContext) => {
    dispatch({ type: 'START_QUICK', context });
  }, []);
  const startGuidedTreatmentPlanFlow = useCallback((context: TreatmentPlanGuidedContext) => {
    dispatch({ type: 'START_GUIDED', context });
  }, []);
  const startTreatmentPlanRevisionFlow = useCallback(() => {
    dispatch({ type: 'START_REVISION' });
  }, []);
  const startProgressNoteGenerateFlow = useCallback(() => {
    dispatch({ type: 'START_PROGRESS_NOTE' });
  }, []);
  const openGeneralChat = useCallback(() => dispatch({ type: 'OPEN_GENERAL_CHAT' }), []);
  const promoteDocumentIdentity = useCallback((documentKey: string, editorSessionKey: string) => {
    dispatch({ type: 'PROMOTE_DOCUMENT_KEY', documentKey, editorSessionKey });
  }, []);
  const clearAllContext = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), []);

  const hasUsableContext = Boolean(
    state.revisionContext || state.guidedContext || state.progressNoteContext,
  );

  const value = useMemo<AiAssistantContextValue>(
    () => ({
      open: state.open,
      width,
      flow: state.flow,
      revisionContext: state.revisionContext,
      guidedContext: state.guidedContext,
      progressNoteContext: state.progressNoteContext,
      activeSession: state.activeSession,
      hasUsableContext,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      setWidth,
      resetWidth,
      startQuickTreatmentPlanFlow,
      startGuidedTreatmentPlanFlow,
      startTreatmentPlanRevisionFlow,
      startProgressNoteGenerateFlow,
      openGeneralChat,
      registerRevisionContext,
      registerGuidedContext,
      registerProgressNoteContext,
      promoteDocumentIdentity,
      clearAllContext,
    }),
    [
      state,
      width,
      hasUsableContext,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      setWidth,
      resetWidth,
      startQuickTreatmentPlanFlow,
      startGuidedTreatmentPlanFlow,
      startTreatmentPlanRevisionFlow,
      startProgressNoteGenerateFlow,
      openGeneralChat,
      registerRevisionContext,
      registerGuidedContext,
      registerProgressNoteContext,
      promoteDocumentIdentity,
      clearAllContext,
    ],
  );

  return <AiAssistantContext.Provider value={value}>{children}</AiAssistantContext.Provider>;
}

export function useAiAssistant(): AiAssistantContextValue {
  const ctx = useContext(AiAssistantContext);
  if (!ctx) throw new Error('useAiAssistant must be used within AiAssistantProvider');
  return ctx;
}

export function useOptionalAiAssistant(): AiAssistantContextValue | null {
  return useContext(AiAssistantContext);
}

export function isRevisionContext(
  context: AiAssistantRegisteredContext | null | undefined,
): context is TreatmentPlanRevisionContext {
  return context?.kind === 'treatment-plan-revision';
}
